import { clsx } from 'clsx';
import React, { useEffect, useState } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { RightCircleFilled } from '@ant-design/icons';
import { Link, useNotify, useRefresh } from 'react-admin';
import { Button } from '@mui/material';

import { creditHttp, doctypeHttp } from '@network';
import { Credit, CreditStatus, Doctype } from '@types';
import { InitialCard, LightTooltip } from '@components';
import { rbac, showAxiosError } from '@utils';
import cls from './credit-contracts-documents.module.css';

interface CreditContractsDocumentsProps {
  credit: Credit;
  editable?: boolean;
}

export const CreditContractsDocuments = ({ credit, editable }: CreditContractsDocumentsProps) => {
  const refresh = useRefresh();
  const notify = useNotify();
  const [doctypes, setDoctypes] = useState<Doctype[]>([]);
  const [loading, setLoading] = useState(false);

  const [contractsStatus, setContractsStatus] = useState({
    creditContract: false,
    amortizationTable: false,
    retentionLetter: false,
  });

  useEffect(() => {
    credit && (async () => {
      const res = await doctypeHttp.getAll();
      if (res) {
        setDoctypes(res);
      }
    })();
  }, [credit]);

  useEffect(() => {
    if (doctypes.length) {
      const creditContract = doctypes.find(d => d.isCreditContract);
      const amortizationTable = doctypes.find(d => d.isAmortizationTable);
      const retentionLetter = doctypes.find(d => d.isRetentionLetter);

      const isCreditContractUploaded = credit.documents
        .find(d => d.doctypeId === creditContract?.id);
      const isAmortizationTableUploaded = credit.documents
        .find(d => d.doctypeId === amortizationTable?.id);
      const isRetentionLetterUploaded = credit.documents
        .find(d => d.doctypeId === retentionLetter?.id);

      setContractsStatus({
        creditContract: !!isCreditContractUploaded?.files?.length,
        amortizationTable: !!isAmortizationTableUploaded?.files?.length,
        retentionLetter: !!isRetentionLetterUploaded?.files?.length,
      });
    }
  }, [doctypes]);

  const renderStatusIcon = (value: boolean) => {
    if (!value) return <CloseIcon style={{ fontSize: 14 }} />;

    return <CheckIcon style={{ fontSize: 14 }} />;
  };

  const requestContract = async () => {
    if (!editable || loading) return;
    try {
      setLoading(true);
      await creditHttp.requestContract(credit.id);
      notify('Contract is requested');
      refresh();
    } catch (err: any) {
      showAxiosError(err);
    } finally {
      setLoading(false);
    }
  };

  const hasAreaSales = rbac.hasAreaSales();
  const requestBtnIsActive = credit.status === CreditStatus.IN_NEGOTIATIONS
    && hasAreaSales
    && !credit.requestContract;

  let requestBtnTitle = '';
  if (!requestBtnIsActive && credit.status !== CreditStatus.IN_NEGOTIATIONS) {
    requestBtnTitle = 'Requesting contract is available only in \'In Negotiations\' status';
  }
  if (!requestBtnIsActive && !hasAreaSales) {
    requestBtnTitle = 'Only Sales Team can request contract';
  }
  if (!requestBtnIsActive && credit.requestContract) {
    requestBtnTitle = 'Contract is already requested';
  }

  return (
    <InitialCard
      left="Contracts"
      right={editable ? (
        <Link to={`/credit/${credit.id}/?contracts`}>
          <RightCircleFilled className={cls.headerIcon} />
        </Link>
      ) : undefined}
      contentClass={cls._}
      wrapperClass={cls.cardWrapper}
    >
      <div className={cls.title}>
        <div className={cls.accredited}>{credit.accreditedName}</div>
        <div>{credit.code}</div>
      </div>
      <div className={cls.content}>
        <div className={cls.item}>
          <div className={cls.itemTitle}>Credit Contract</div>
          <div className={clsx(cls.itemIcon, contractsStatus.creditContract && cls.success)}>
            {renderStatusIcon(contractsStatus.creditContract)}
          </div>
        </div>
        <div className={cls.item}>
          <div className={cls.itemTitle}>Amortization table</div>
          <div className={clsx(cls.itemIcon, contractsStatus.amortizationTable && cls.success)}>
            {renderStatusIcon(contractsStatus.amortizationTable)}
          </div>
        </div>
        <div className={cls.item}>
          <div className={cls.itemTitle}>Retention Letter</div>
          <div className={clsx(cls.itemIcon, contractsStatus.retentionLetter && cls.success)}>
            {renderStatusIcon(contractsStatus.retentionLetter)}
          </div>
        </div>
      </div>

      <div className={cls.request}>
        {requestBtnTitle ? (
          <LightTooltip extraWidth title={requestBtnTitle}>
            <div>
              <Button
                color="secondary"
                variant="contained"
                className={cls.requestBtn}
                onClick={requestBtnIsActive ? requestContract : undefined}
                disabled={!editable || !requestBtnIsActive}
              >
                Request Contract
              </Button>
            </div>
          </LightTooltip>
        ) : (
          <Button
            color="secondary"
            variant="contained"
            className={cls.requestBtn}
            onClick={requestBtnIsActive ? requestContract : undefined}
            disabled={!editable || !requestBtnIsActive}
          >
            Request Contract
          </Button>
        )}
      </div>
    </InitialCard>
  );
};
